import path from "path";
import fs from "fs";

/**
 * Determines if a package name is a native module.
 * Uses the node_modules filesystem first, then falls back to a static registry.
 */
export function isDependencyNative(projectRoot: string, packageName: string): boolean {
  const depPath = path.join(projectRoot, "node_modules", packageName);

  if (fs.existsSync(depPath)) {
    // Check for ios/android native folders
    if (
      fs.existsSync(path.join(depPath, "ios")) ||
      fs.existsSync(path.join(depPath, "android"))
    ) {
      return true;
    }
    // Check for a .podspec file
    try {
      const files = fs.readdirSync(depPath);
      if (files.some((f) => f.endsWith(".podspec"))) {
        return true;
      }
    } catch {
      // ignore
    }
    return false;
  }

  // Fallback: static registry of well-known native packages
  const knownNativePrefixes = [
    "react-native-maps",
    "react-native-reanimated",
    "react-native-gesture-handler",
    "react-native-screens",
    "react-native-safe-area-context",
    "react-native-vision-camera",
    "react-native-mmkv",
    "react-native-webview",
    "react-native-vector-icons",
    "react-native-device-info",
    "react-native-camera",
    "react-native-image-picker",
    "react-native-permissions",
    "react-native-async-storage",
    "react-native-svg",
    "react-native-linear-gradient",
    "react-native-splash-screen",
    "react-native-push-notification",
    "@react-native-firebase",
    "@react-native-community",
    "@react-native-async-storage",
  ];

  return knownNativePrefixes.some(
    (prefix) => packageName === prefix || packageName.startsWith(prefix + "/")
  );
}

/**
 * Analyzes a project's dependencies, counting totals and identifying native modules.
 */
export interface DependencyAnalysis {
  totalDependencies: number;
  nativeModules: string[];
  reactNativeVersion: string;
  reactVersion: string;
  typesReactNativeVersion: string;
  allDeps: Record<string, string>;
}

export function analyzeDependencies(
  projectRoot: string,
  pkg: Record<string, unknown>
): DependencyAnalysis {
  const dependencies = (pkg["dependencies"] as Record<string, string>) || {};
  const devDependencies = (pkg["devDependencies"] as Record<string, string>) || {};
  const allDeps: Record<string, string> = { ...dependencies, ...devDependencies };

  const totalDependencies = Object.keys(allDeps).length;

  const nativeModules: string[] = [];
  for (const depName of Object.keys(dependencies)) {
    if (depName !== "react-native" && isDependencyNative(projectRoot, depName)) {
      nativeModules.push(depName);
    }
  }

  const rnVersionSpec = allDeps["react-native"] || "";
  const reactNativeVersion = rnVersionSpec.replace(/^[^0-9]*/, "") || "unknown";

  const reactVersionSpec = allDeps["react"] || "";
  const reactVersion = reactVersionSpec.replace(/^[^0-9]*/, "") || "";

  const typesRnSpec = allDeps["@types/react-native"] || "";
  const typesReactNativeVersion = typesRnSpec.replace(/^[^0-9]*/, "") || "";

  return {
    totalDependencies,
    nativeModules,
    reactNativeVersion,
    reactVersion,
    typesReactNativeVersion,
    allDeps,
  };
}
